<template>
  <div class="mini-tab-content">
    <div
      v-if="loadingRegulations"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="primary"
      />
      <span>Carregando...</span>
    </div>
    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="FileTextIcon"
            size="13"
            class="mr-25 text-primary"
          />
          {{ regulations.length ? regulations.length + ' regulação(ões)' : 'Nenhuma regulação' }}
        </span>
        <button
          class="btn btn-sm btn-primary"
          @click="showRegulationModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          Registrar
        </button>
      </div>

      <div
        v-if="!regulations.length"
        class="interc-empty"
      >
        <feather-icon
          icon="FileTextIcon"
          size="26"
        />
        <p>Nenhuma regulação registrada.</p>
      </div>

      <div
        v-if="regulations.length"
        class="interc-list"
      >
        <div
          v-for="mr in regulations"
          :key="mr.id"
          :class="['interc-item', { 'interc-item--cancelled': mr.cancelled_by_user_id }]"
        >
          <div class="interc-item__top">
            <div class="interc-item__badge">
              <feather-icon
                :icon="mr.cancelled_by_user_id ? 'XCircleIcon' : 'FileTextIcon'"
                size="11"
                class="mr-25"
              />
              Regulação
            </div>
            <button
              v-if="!mr.cancelled_by_user_id"
              class="interc-item__del"
              title="Cancelar registro"
              @click="deleteRegulation(mr.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="13"
              />
            </button>
          </div>
          <div
            class="evolution-content"
            v-html="mr.description"
          />
          <div class="interc-item__meta">
            <feather-icon
              icon="UserIcon"
              size="11"
            />
            <span>{{ mr.user ? mr.user.name : '—' }}</span>
            <span class="interc-item__dot" />
            <feather-icon
              icon="ClockIcon"
              size="11"
            />
            <span>{{ mr.created_at | formatDate }}</span>
          </div>
          <div
            v-if="mr.cancelled_by_user_id"
            class="interc-item__cancelled-row"
          >
            <feather-icon
              icon="XCircleIcon"
              size="11"
              class="mr-25"
            />
            Cancelado por
            <strong>{{ mr.cancelled_by ? mr.cancelled_by.name : '—' }}</strong>
            · {{ mr.cancelled_at | formatDate }}
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showRegulationModal"
      title="Registrar Regulação"
      :title-class="'font-weight-bold'"
      size="lg"
      centered
      lazy
      no-enforce-focus
      no-close-on-backdrop
      @hidden="newRegulation = { description: '' }"
    >
      <div class="form-group mb-1">
        <label class="interc-label"><span class="required">*</span> Regulação</label>
        <ckeditor
          :value="newRegulation.description"
          :editor="editor"
          :config="editorConfig"
          @input="newRegulation.description = $event"
        />
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showRegulationModal = false"
        >
          Cancelar
        </button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="savingRegulation || !regulationHasContent"
          @click="saveRegulation"
        >
          <b-spinner
            v-if="savingRegulation"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="FileTextIcon"
            size="13"
            class="mr-25"
          />
          Registrar
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import involvedPanelMixin from './involvedPanelMixin'

export default {
  name: 'RegulationPanel',
  components: { ckeditor: CKEditor.component },
  mixins: [involvedPanelMixin],
  data() {
    return {
      regulations: [],
      loadingRegulations: false,
      showRegulationModal: false,
      savingRegulation: false,
      newRegulation: { description: '' },
      editorConfig: {
        toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|', 'undo', 'redo'],
      },
    }
  },
  computed: {
    regulationHasContent() {
      // Remove tags HTML para não habilitar o botão com editor "vazio" (<p></p>, &nbsp;).
      const text = (this.newRegulation.description || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
      return text.length > 0
    },
  },
  // Mantém o build do CKEditor FORA da reatividade do Vue (boa prática oficial):
  // se ClassicEditor for observado pelo Vue, a instância pode quebrar e parar de emitir input.
  created() {
    this.editor = ClassicEditor
  },
  mounted() {
    this.loadRegulations()
  },
  methods: {
    async loadRegulations() {
      this.loadingRegulations = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_regulations`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.regulations = res.data.data || []
      } catch (e) {
        this.regulations = []
        this.$message('Erro', 'Não foi possível carregar as regulações.', 'error')
      } finally {
        this.loadingRegulations = false
      }
    },
    async saveRegulation() {
      if (!this.regulationHasContent) return
      this.savingRegulation = true
      try {
        const { user } = this.$store.state
        const res = await this.$http.post(`${this.$store.state.api}occurrence_regulations`, {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          user_id: user.id,
          description: this.newRegulation.description,
        })
        const saved = res.data.data || res.data
        this.regulations.push(saved)
        this.newRegulation = { description: '' }
        this.showRegulationModal = false
        this.$message('Regulação registrada', 'A regulação foi salva com sucesso.', 'success')
      } catch (e) {
        this.$message('Erro', 'Erro ao registrar regulação.', 'error')
      } finally {
        this.savingRegulation = false
      }
    },
    async deleteRegulation(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'O registro ficará marcado como cancelado e será mantido no histórico. Deseja continuar?',
        {
          title: 'Cancelar regulação', okTitle: 'Sim, cancelar', cancelTitle: 'Voltar', okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_regulations/${id}`)
        const updated = res.data.data || res.data
        const idx = this.regulations.findIndex(mr => mr.id === id)
        if (idx !== -1) this.$set(this.regulations, idx, updated)
      } catch (e) {
        this.$message('Erro', 'Erro ao cancelar regulação.', 'error')
      }
    },
  },
}
</script>
